import { useState } from 'react';
import { 
  CheckCircle, Zap, Globe, Shield, Clock, Server, Mail, 
  Settings, LayoutTemplate, Replace, Search, ShoppingCart, 
  Cloud,  TrendingUp 
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PricingPage = () => {
  const [isBasicPackage, setIsBasicPackage] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('development');

  const developmentPlans = [
    {
      name: 'Basic',
      price: isBasicPackage ? '299' : '499',
      description: isBasicPackage ? 'Basic customization for starter projects' : 'Extended package with enhanced support',
      features: isBasicPackage ? [
        { icon: <LayoutTemplate className="w-5 h-5" />, text: 'Template customization' },
        { icon: <Settings className="w-5 h-5" />, text: 'Basic installation' },
        { icon: <Replace className="w-5 h-5" />, text: 'Replacement of up to 5 assets' },
        { icon: <Mail className="w-5 h-5" />, text: 'Email support (48h)' },
      ] : [
        { icon: <LayoutTemplate className="w-5 h-5" />, text: 'Advanced customization' },
        { icon: <Settings className="w-5 h-5" />, text: 'Premium installation' },
        { icon: <Replace className="w-5 h-5" />, text: 'Unlimited asset replacement' },
        { icon: <Clock className="w-5 h-5" />, text: 'Priority support (24h)' },
      ],
      popular: false
    },
    {
      name: 'Professional',
      price: isBasicPackage ? '799' : '1,299',
      description: isBasicPackage ? 'Professional solution for growing businesses' : 'Complete enterprise services',
      features: isBasicPackage ? [
        { icon: <LayoutTemplate className="w-5 h-5" />, text: 'Advanced customization' },
        { icon: <Shield className="w-5 h-5" />, text: 'Secure configuration' },
        { icon: <Replace className="w-5 h-5" />, text: 'Replacement of 15 assets' },
        { icon: <Clock className="w-5 h-5" />, text: '24/7 support' },
      ] : [
        { icon: <Zap className="w-5 h-5" />, text: 'Premium customization' },
        { icon: <Globe className="w-5 h-5" />, text: 'Global installation' },
        { icon: <Replace className="w-5 h-5" />, text: 'Comprehensive asset management' },
        { icon: <Server className="w-5 h-5" />, text: 'Dedicated support' },
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      description: 'Tailored solution for large-scale needs',
      features: [
        { icon: <Zap className="w-5 h-5" />, text: 'Custom development' },
        { icon: <Globe className="w-5 h-5" />, text: 'Global implementation' },
        { icon: <Shield className="w-5 h-5" />, text: 'Enterprise security' },
        { icon: <Server className="w-5 h-5" />, text: 'Infrastructure management' },
      ],
      popular: false
    }
  ];

  const supportPlans = [
    {
      name: 'Corporate Email',
      price: '199',
      description: 'Professional setup for business emails',
      features: [
        { icon: <Mail className="w-5 h-5" />, text: '10 email accounts' },
        { icon: <Shield className="w-5 h-5" />, text: 'Anti-spam protection' },
        { icon: <Globe className="w-5 h-5" />, text: 'Custom domain' },
        { icon: <Settings className="w-5 h-5" />, text: 'Configuration support' },
      ],
      popular: false
    },
    {
      name: 'Advanced SEO',
      price: '599',
      description: 'Optimization for search engines',
      features: [
        { icon: <Search className="w-5 h-5" />, text: 'Keyword analysis' },
        { icon: <Zap className="w-5 h-5" />, text: 'Technical optimization' },
        { icon: <TrendingUp className="w-5 h-5" />, text: 'Performance reports' },
        { icon: <Clock className="w-5 h-5" />, text: 'Continuous monitoring' },
      ],
      popular: false
    },
    {
      name: 'E-commerce',
      price: '1,499',
      description: 'Complete solution for online stores',
      features: [
        { icon: <ShoppingCart className="w-5 h-5" />, text: 'Gateway integration' },
        { icon: <Shield className="w-5 h-5" />, text: 'SSL certificate included' },
        { icon: <Cloud className="w-5 h-5" />, text: 'Optimized hosting' },
        { icon: <Cloud className="w-5 h-5" />, text: 'Inventory management' },
      ],
      popular: true
    },
    {
      name: 'Cloud Hosting',
      price: '129',
      description: 'High-performance hosting',
      features: [
        { icon: <Cloud className="w-5 h-5" />, text: '100GB SSD Storage' },
        { icon: <Zap className="w-5 h-5" />, text: 'Global CDN' },
        { icon: <Shield className="w-5 h-5" />, text: 'Daily backups' },
        { icon: <Server className="w-5 h-5" />, text: 'Auto-scaling' },
      ],
      popular: false
    },
    {
      name: 'Maintenance',
      price: '99/month',
      description: 'Continuous technical support',
      features: [
        { icon: <Cloud className="w-5 h-5" />, text: 'Regular updates' },
        { icon: <Shield className="w-5 h-5" />, text: 'Antivirus protection' },
        { icon: <Clock className="w-5 h-5" />, text: '24/7 support' },
        { icon: <Cloud className="w-5 h-5" />, text: 'Monthly reports' },
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Servicios <span className="bg-gradient-to-r from-[#1e3a8a] to-[#334E7D] bg-clip-text text-transparent">Profesionales</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Seleccione el tipo de servicio que necesita
          </p>

          {/* Primer Toggle: Categoría de servicio */}
          <div className="mt-8 flex items-center justify-center gap-4 mb-12">
            <button
              onClick={() => setSelectedCategory('development')}
              className={`px-6 py-2 rounded-full font-medium ${
                selectedCategory === 'development'
                  ? 'bg-[#1e3a8a] text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Desarrollo
            </button>
            <button
              onClick={() => setSelectedCategory('support')}
              className={`px-6 py-2 rounded-full font-medium ${
                selectedCategory === 'support'
                  ? 'bg-[#1e3a8a] text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Soporte Técnico
            </button>
          </div>

          {/* Segundo Toggle: Solo para Desarrollo */}
          {selectedCategory === 'development' && (
            <div className="flex items-center justify-center gap-4 mb-12">
              <span className="text-gray-500">Paquete Básico</span>
              <button 
                onClick={() => setIsBasicPackage(!isBasicPackage)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  !isBasicPackage ? 'bg-[#1e3a8a]' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  !isBasicPackage ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
              <span className="text-gray-500">Paquete Completo</span>
            </div>
          )}
        </div>

        {/* Tarjetas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {(selectedCategory === 'development' ? developmentPlans : supportPlans).map((plan, index) => (
            <div 
              key={index}
              className={`relative bg-white rounded-2xl shadow-lg p-8 border-2 ${
                plan.popular 
                  ? 'border-[#334E7D] shadow-[#334E7D]/20' 
                  : 'border-gray-200 hover:border-[#1e3a8a]/40 transition-all'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 -mt-4 -mr-4 bg-[#334E7D] text-white px-4 py-1 rounded-full text-sm font-medium">
                  Más Popular
                </div>
              )}
              
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600">{plan.description}</p>
              </div>

              <div className="mb-8">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {typeof plan.price === 'string' ? plan.price : `$${plan.price}`}
                  {typeof plan.price !== 'string' && (
                    <span className="text-lg text-gray-500">/proyecto</span>
                  )}
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#1e3a8a] flex-shrink-0" />
                    <span className="text-gray-600">{feature.text}</span>
                  </li>
                ))}
              </ul>

              <button 
                className={`w-full py-3 px-6 rounded-lg font-medium transition-all ${
                  plan.popular 
                    ? 'bg-[#334E7D] text-white hover:bg-[#1c3d6e]' 
                    : 'bg-[#f0f4f9] text-[#1e3a8a] hover:bg-[#e0e9f4]'
                }`}
              >
                Contratar
              </button>
            </div>
          ))}
        </div>

        {/* Información Adicional */}
        <div className="mt-16 text-center text-gray-600">
          <p className="mb-4">¿Necesitas algo personalizado? <Link to="/contact" className="text-[#1e3a8a] hover:underline">Contáctanos</Link></p>
          <div className="flex items-center justify-center gap-6">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#334E7D]" />
              <span>Garantía de satisfacción</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#1e3a8a]" />
              <span>Implementación rápida</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;